import { isEmpty } from '@ember/utils';
import CampaignModel from 'cio/models/campaign';
import TemplateModel from 'cio/models/template';
import { Editor } from 'cio/services/editor';
import { findUsedVariablePaths as findUsedLiquidVariablePaths } from '../liquid';
import { findUsedVariablePaths as findUsedJSVariablePaths } from '../js-ast';
import { keyPathToString } from '../objects';

export enum DataActionConditionType {
  TriggerAttribute = 'trigger_attribute',
  ProfileAttribute = 'profile_attribute',
  EventAttribute = 'event_attribute',
  Liquid = 'liquid',
  Static = 'static',
  RemoveAttribute = 'remove_attribute',
  JavaScript = 'javascript',
}

export const DATA_ACTION_CONDITION_TYPE_LABEL = {
  [DataActionConditionType.Static]: 'Static value',
  [DataActionConditionType.TriggerAttribute]: 'Trigger attribute',
  [DataActionConditionType.ProfileAttribute]: 'Profile attribute',
  [DataActionConditionType.EventAttribute]: 'Event attribute',
  [DataActionConditionType.Liquid]: 'Liquid',
  [DataActionConditionType.RemoveAttribute]: 'Remove attribute',
  [DataActionConditionType.JavaScript]: 'JavaScript',
};

export const DATA_ACTION_CONDITION_TYPE_ICON: Partial<Record<DataActionConditionType, string>> = {
  [DataActionConditionType.TriggerAttribute]: 'object',
  [DataActionConditionType.EventAttribute]: 'event',
  [DataActionConditionType.ProfileAttribute]: 'person',
  [DataActionConditionType.JavaScript]: 'editor-code',
};

export function getTypeIcon(type: DataActionConditionType) {
  return DATA_ACTION_CONDITION_TYPE_ICON[type] ?? null;
}

export const ATTRIBUTE_CONDITION_TYPES = [
  DataActionConditionType.TriggerAttribute,
  DataActionConditionType.ProfileAttribute,
  DataActionConditionType.EventAttribute,
];

export const EDITABLE_CONDITION_TYPES = [
  DataActionConditionType.TriggerAttribute,
  DataActionConditionType.ProfileAttribute,
  DataActionConditionType.EventAttribute,
  DataActionConditionType.Liquid,
  DataActionConditionType.Static,
  DataActionConditionType.JavaScript,
];

export function isTypeEditable(type: DataActionConditionType) {
  return EDITABLE_CONDITION_TYPES.includes(type);
}

export type DataActionTriggerAttributeDropdownItem = {
  label: string;
  value: string;
  path: (string | number)[];
};

export type CreateEventEditorType = Editor.DEFAULT | Editor.JSON | Editor.JS;

export enum DataActionComposerSectionType {
  Recipient = 'recipient',
  BodyRow = 'body_row',
  // Send Event action specific types:
  EventName = 'event_name',
  BodyJSON = 'create_event_body_json',
}

export function getSupportedConditionTypes(campaign: CampaignModel) {
  if (campaign.isDataCampaign) {
    return [
      DataActionConditionType.Static,
      DataActionConditionType.TriggerAttribute,
      DataActionConditionType.Liquid,
      DataActionConditionType.JavaScript,
    ];
  }

  if (campaign.isEventTriggered) {
    return [
      DataActionConditionType.Static,
      DataActionConditionType.ProfileAttribute,
      DataActionConditionType.EventAttribute,
      DataActionConditionType.Liquid,
      DataActionConditionType.JavaScript,
    ];
  }

  if (campaign.isFormTriggered) {
    return [
      DataActionConditionType.Static,
      DataActionConditionType.ProfileAttribute,
      DataActionConditionType.EventAttribute,
      DataActionConditionType.Liquid,
      DataActionConditionType.JavaScript,
    ];
  }

  if (campaign.isApiTriggered) {
    return [
      DataActionConditionType.Static,
      DataActionConditionType.ProfileAttribute,
      DataActionConditionType.TriggerAttribute,
      DataActionConditionType.Liquid,
      DataActionConditionType.JavaScript,
    ];
  }

  return [
    DataActionConditionType.Static,
    DataActionConditionType.ProfileAttribute,
    DataActionConditionType.Liquid,
    DataActionConditionType.JavaScript,
  ];
}

export function getDefaultConditionType(campaign: CampaignModel) {
  if (campaign.isDataCampaign) {
    return DataActionConditionType.TriggerAttribute;
  }

  if (campaign.isEventTriggered) {
    return DataActionConditionType.EventAttribute;
  }

  if (campaign.isApiTriggered) {
    return DataActionConditionType.TriggerAttribute;
  }

  return DataActionConditionType.ProfileAttribute;
}

export const AUTOCOMPLETE_INPUT_CONDITION_TYPES = [
  DataActionConditionType.TriggerAttribute,
  DataActionConditionType.ProfileAttribute,
  DataActionConditionType.EventAttribute,
];

export const ASYNC_AUTOCOMPLETE_TYPES = [DataActionConditionType.EventAttribute];

export type DataActionAutocompleteValuesContainer = Partial<
  Record<DataActionConditionType, { label: string; value: string; path: string[] }[] | string[]>
>;

export interface CreateEventComposerSection {
  type: DataActionConditionType;
  value: string;
}

export function getTopLevelKeyFromConditionType(type: DataActionConditionType) {
  return {
    [DataActionConditionType.TriggerAttribute]: 'trigger',
    [DataActionConditionType.ProfileAttribute]: 'customer',
    [DataActionConditionType.EventAttribute]: 'event',
    [DataActionConditionType.Liquid]: null,
    [DataActionConditionType.Static]: null,
    [DataActionConditionType.RemoveAttribute]: null,
    [DataActionConditionType.JavaScript]: null,
  }[type];
}

export function getConditionTypeFromTopLevelKey(key: 'customer' | 'event' | 'trigger'): DataActionConditionType {
  switch (key) {
    case 'customer':
      return DataActionConditionType.ProfileAttribute;
    case 'event':
      return DataActionConditionType.EventAttribute;
    case 'trigger':
      return DataActionConditionType.TriggerAttribute;
  }
}

export async function extractUsedPaths(template: TemplateModel, sections: CreateEventComposerSection[]) {
  let pathValues: string[] = [];
  let liquidValues: string[] = [];
  let jsValues: string[] = [];

  if (template.editor === Editor.JSON) {
    liquidValues.push(template.body);
  } else if (template.editor === Editor.JS) {
    jsValues.push(template.body);
  }

  sections.forEach((section) => {
    if (ATTRIBUTE_CONDITION_TYPES.includes(section.type)) {
      pathValues.push(section.value);
    } else if (section.type === DataActionConditionType.Liquid) {
      liquidValues.push(section.value);
    } else if (section.type === DataActionConditionType.JavaScript) {
      jsValues.push(section.value);
    }
  });

  pathValues.push(...liquidValuesToPaths(liquidValues));
  pathValues.push(...(await jsValuesToPaths(jsValues)));

  return pathValues.uniq().filter((value) => !isEmpty(value));
}

function liquidValuesToPaths(values: string[]) {
  return values.reduce<string[]>((acc, value) => {
    let paths = liquidValueToPaths(value) ?? [];
    return [...acc, ...paths];
  }, []);
}

function liquidValueToPaths(value: string) {
  let variablePathsInLiquid = findUsedLiquidVariablePaths('trigger', value);
  return variablePathsInLiquid.map((path) => keyPathToString(path.slice(1)));
}

async function jsValuesToPaths(values: string[]) {
  let paths: string[] = [];

  async function findAndSavePaths(value: string) {
    let pathsInValue = await jsValueToPaths(value);
    paths.push(...pathsInValue);
  }

  await Promise.all(values.map(findAndSavePaths));
  return paths;
}

async function jsValueToPaths(value: string) {
  let variablePathsInJs = await findUsedJSVariablePaths(value);
  let onlyTriggerPaths = variablePathsInJs.filter((path) => path[0] === 'trigger');
  return onlyTriggerPaths.map((path) => keyPathToString(path.slice(1) as (string | number)[]));
}
